import { stripPhoneSeparators } from './internal';

export const E164_PATTERN = /^\+[0-9]{8,15}$/;
export const BRAZIL_COUNTRY_CODE = '55';
/** DDD + assinante: 10 é fixo, 11 é celular. */
export const BRAZIL_NATIONAL_NUMBER_LENGTHS: readonly number[] = Object.freeze([10, 11]);

export interface CanonicalizePhoneOptions {
  readonly defaultCountryCode?: string;
}

function resolveCandidate(stripped: string, countryCode: string): string | null {
  if (stripped.startsWith('+')) return stripped;
  if (!/^[0-9]+$/.test(stripped)) return null;
  if (BRAZIL_NATIONAL_NUMBER_LENGTHS.includes(stripped.length)) return `+${countryCode}${stripped}`;
  if (stripped.startsWith(countryCode)) return `+${stripped}`;
  return null;
}

/** Telefone em E.164 (`+5511987654321`) ou `null`. Número nacional sem DDI assume Brasil. */
export function canonicalizePhone(input: string, options: CanonicalizePhoneOptions = {}): string | null {
  const countryCode = options.defaultCountryCode ?? BRAZIL_COUNTRY_CODE;
  const candidate = resolveCandidate(stripPhoneSeparators(input), countryCode);
  if (candidate === null || !E164_PATTERN.test(candidate)) return null;
  if (!candidate.startsWith(`+${BRAZIL_COUNTRY_CODE}`)) return candidate;
  const national = candidate.slice(1 + BRAZIL_COUNTRY_CODE.length);
  return BRAZIL_NATIONAL_NUMBER_LENGTHS.includes(national.length) ? candidate : null;
}

export function isValidPhone(input: string, options: CanonicalizePhoneOptions = {}): boolean {
  return canonicalizePhone(input, options) !== null;
}

export function isBrazilianPhone(e164: string): boolean {
  if (!E164_PATTERN.test(e164) || !e164.startsWith(`+${BRAZIL_COUNTRY_CODE}`)) return false;
  return BRAZIL_NATIONAL_NUMBER_LENGTHS.includes(e164.length - 1 - BRAZIL_COUNTRY_CODE.length);
}

export function isBrazilianMobile(e164: string): boolean {
  return isBrazilianPhone(e164) && e164.length === 1 + BRAZIL_COUNTRY_CODE.length + 11;
}

/** DDD + assinante (`11987654321`) de um telefone brasileiro, ou `null`. */
export function nationalNumberBr(input: string): string | null {
  const canonical = canonicalizePhone(input);
  if (canonical === null || !isBrazilianPhone(canonical)) return null;
  return canonical.slice(1 + BRAZIL_COUNTRY_CODE.length);
}

/** `(11) 98765-4321` para exibição; E.164 estrangeiro volta como veio; inválido vira `null`. */
export function formatPhoneBr(input: string): string | null {
  const canonical = canonicalizePhone(input);
  if (canonical === null) return null;
  if (!isBrazilianPhone(canonical)) return canonical;
  const national = canonical.slice(1 + BRAZIL_COUNTRY_CODE.length);
  const subscriber = national.slice(2);
  const splitAt = isBrazilianMobile(canonical) ? 5 : 4;
  return `(${national.slice(0, 2)}) ${subscriber.slice(0, splitAt)}-${subscriber.slice(splitAt)}`;
}
